import React, { useEffect, useState, useMemo } from 'react';
import './BadgesSection.css';
import { apiFetch } from '../utils/serverConfig';
import { ipfsUrl } from '../utils/pinata';

// Logic-based badges (auto-unlock by total classes attended)
const LOGIC_BADGES = [
  { id: 'first_class', name: 'First Class', required: 1, cid: 'bafybeiewacofxkrgrhbdutojpe3kamud265gw2pyodrum7nlsugb5pn2su', type: 'logic', order: 10 },
  { id: '10_classes', name: '10 Classes', required: 10, cid: 'bafybeibg3fneb2vo3vvkuaf4rxhhgipxqxldideduw744fj2w6heiteu4e', type: 'logic', order: 20 },
  // 7-day streak badge (requires consecutive attendance days)
  { id: '7_classes', name: '7 Classes', required: 7, cid: 'bafybeibg3fneb2vo3vvkuaf4rxhhgipxqxldideduw744fj2w6heiteu4e', type: 'logic', order: 30, requiresStreak: true },
  // 14-day streak badge (requires consecutive attendance days)
  { id: '14_classes', name: '14 Days', required: 14, cid: 'bafybeie6gzcyanqyfp4d5jqwn2e42uxkeaottp727dw6ujgfokac3haqhy', type: 'logic', order: 35, requiresStreak: true },
  { id: '20_classes', name: '20 Classes', required: 20, cid: 'bafybeihdnzmqcheehq7wuyqlkesjduodply7wixbp4gh57achw634ydqlm', type: 'logic', order: 40 },
  { id: '500_classes', name: '500 Classes', required: 500, cid: 'bafybeibsxzvojxw55r7at5vssgqqrti55y7itcuhthd6as4eiusdrwvl7e', type: 'logic', order: 90 },
  { id: '1000_classes', name: '1000 Classes', required: 1000, cid: 'bafybeidhbhcfa7g3rx5fyi3ep7gakf5ckvnq25nf45mtl7yyqa3mjsje2m', type: 'logic', order: 100 }
];

// Admin-only manual badges (appear first)
const ADMIN_BADGES = {
  'coach': { id: 'coach', name: 'Coach', cid: 'bafybeicgupy7xm5qwcwjsaludeflb4krfqnsazii6irkabeumsaqt26kle', type: 'admin', order: 0 },
  'doctor-green': { id: 'doctor-green', name: 'Doctor Green', cid: 'bafybeiffwbyjqv2hmmhhp7l6zip34oendjhzucyfop74d5v6xtv5ofzbki', type: 'admin', order: 1 },
  'baddie': { id: 'baddie', name: 'Baddie', cid: 'bafybeigkxcvchmzosn6sizba3c3kxv3imshqmqurcexmoqj3tndhut4joi', type: 'admin', order: 2 }
  , 'super_baddie': { id: 'super_baddie', name: 'Super Baddie', cid: 'bafybeibwtpvmu4aazxzfzr6ix45nxyer5aydvxqvv7awpau4h2plqv7v2y', type: 'admin', order: 3 }
  , 'master_class': { id: 'master_class', name: 'Master Class', cid: 'bafybeig5prrwvjk6si3ynml5uzqgszjqtgyj64ruxhyd2jsz65t3j5mn2e', type: 'admin', order: 3 }
  , 'super_student': { id: 'super_student', name: 'Super Student', cid: 'bafybeibwmi6amxqwgr3xdztffxnlzgdimdagiur667kglu6ra3cqr2ymgi', type: 'admin', order: 4 }
  , 'core_student': { id: 'core_student', name: 'Core Student', cid: 'bafybeialtr4hmf363sdz4tqxubxzty5cyz25m2mscuytpsidl6xncizlai', type: 'admin', order: 5 }
  , 'upcoming_beast': { id: 'upcoming_beast', name: 'Upcoming Beast', cid: 'bafybeie2qli6pegdbbw6na4zklt7m2uctywam4hsqmhtbjc5hql2xv2h6y', type: 'admin', order: 6 }
};

function normalizeAttendanceList(attendance, studentId) {
  if (!attendance) return [];
  // attendance may be an array of objects { studentId, dateISO } or an object keyed by studentId or an array of date strings
  if (Array.isArray(attendance)) {
    // array of records
    return attendance.filter(a => {
      if (!a) return false;
      if (typeof a === 'string') return true; // if caller passed student-specific array
      return String(a.studentId || a.id || a.student || '').toString() === String(studentId);
    }).map(a => (typeof a === 'string' ? a : (a.dateISO || a.date || a.createdAt || a.id || ''))).filter(Boolean);
  }
  // object keyed by studentId -> array
  if (typeof attendance === 'object') {
    const arr = attendance[studentId] || attendance[String(studentId)] || [];
    if (Array.isArray(arr)) return arr.map(d => (typeof d === 'string' ? d : (d.dateISO || d.createdAt || ''))).filter(Boolean);
  }
  return [];
}

export default function StudentBadgesTab({ badges = [], student = null, attendance = [] }) {
  const [localBadges, setLocalBadges] = useState(Array.isArray(badges) ? badges.slice() : []);
  const studentId = student?.id || null;

  const studentAttendance = useMemo(() => normalizeAttendanceList(attendance, studentId), [attendance, studentId]);

  // Resolve badges using attendance.totalVisits (or fallback to derived list length)
  const computedBadges = useMemo(() => {
    const totalVisits = (attendance && typeof attendance === 'object' && typeof attendance.totalVisits === 'number')
      ? attendance.totalVisits
      : studentAttendance.length;

    const now = new Date().toISOString();

    // compute current consecutive-day streak from the derived studentAttendance list
    const computeStreak = (attList) => {
      try {
        const days = new Set((attList || []).map(d => (d || '').toString().slice(0,10)));
        let streak = 0;
        const today = new Date();
        while (true) {
          const key = today.toISOString().slice(0,10);
          if (days.has(key)) {
            streak += 1;
            today.setDate(today.getDate() - 1);
          } else break;
        }
        return streak;
      } catch (e) { return 0; }
    };

    const streak = computeStreak(studentAttendance);

    const resolved = [];

    // Admin badges first. Support single `adminBadge` or array `adminBadges`.
    if (student && student.adminBadge && ADMIN_BADGES[student.adminBadge]) {
      resolved.push({ ...ADMIN_BADGES[student.adminBadge], unlocked: true, earnedAt: now });
    }
    if (student && Array.isArray(student.adminBadges)) {
      student.adminBadges.forEach(ab => {
        if (ab && ADMIN_BADGES[ab]) resolved.push({ ...ADMIN_BADGES[ab], unlocked: true, earnedAt: now });
      });
    }

    // Preserve any existing custom badges (not logic/admin)
    const existingCustom = (Array.isArray(badges) ? badges : []).filter(b => !LOGIC_BADGES.find(t => t.id === b.id) && !(b && b.id && ADMIN_BADGES[b.id]));
    existingCustom.forEach(ec => {
      const src = ec.src || (ec.cid ? ipfsUrl(ec.cid) : (ec.image && String(ec.image).startsWith('http') ? ec.image : ''));
      resolved.push({ ...ec, unlocked: true, src });
    });

        // Logic badges: either class-count based or streak-based depending on badge config
    LOGIC_BADGES.forEach(b => {
      if (b.requiresStreak) {
        const progress = Math.min(streak / (b.required || 1), 1);
        resolved.push({
          id: b.id,
          name: b.name,
          src: b.cid ? ipfsUrl(b.cid) : '',
          type: b.type || 'logic',
          required: b.required,
          unlocked: streak >= (b.required || 0),
          progress,
          order: b.order || 999,
          earnedAt: streak >= (b.required || 0) ? now : undefined
        });
      } else {
        const progress = Math.min(totalVisits / (b.required || 1), 1);
        resolved.push({
          id: b.id,
          name: b.name,
          src: b.cid ? ipfsUrl(b.cid) : '',
          type: b.type || 'logic',
          required: b.required,
          unlocked: totalVisits >= (b.required || 0),
          progress,
          order: b.order || 999,
          earnedAt: totalVisits >= (b.required || 0) ? now : undefined
        });
      }
    });

    // Sort final resolved badges by order (admin first)
    resolved.sort((a, b) => (Number(a.order || 999) - Number(b.order || 999)));

    return resolved;
  }, [studentAttendance, badges, attendance, student]);

  // current total visits used for progress bars (source of truth)
  const totalVisits = (attendance && typeof attendance === 'object' && typeof attendance.totalVisits === 'number')
    ? attendance.totalVisits
    : studentAttendance.length;

  // Compare and persist if changed
  useEffect(() => {
    if (!studentId) return;
    const normalizedExisting = (Array.isArray(badges) ? badges : []).map(b => b.id).sort().join(',');
    const normalizedComputed = (Array.isArray(computedBadges) ? computedBadges : []).map(b => b.id).sort().join(',');
    if (normalizedExisting !== normalizedComputed) {
      // update local display immediately
      setLocalBadges(computedBadges);

      // Best-effort persist to server: update only badges field
      (async () => {
        try {
          await apiFetch(`/api/students/${studentId}`, { method: 'PUT', body: JSON.stringify({ updates: { badges: computedBadges } }) });
        } catch (e) {
          console.warn('StudentBadgesTab: failed to persist badges', e);
        }
      })();
    } else {
      // keep local in-sync
      setLocalBadges(Array.isArray(badges) ? badges.slice() : []);
    }
  }, [computedBadges, studentId, badges]);

  return (
    <div>
      <div className="badges-grid">
        {(Array.isArray(localBadges) ? localBadges : []).map((b) => (
          <div key={b.id || b.name} className="badge-card">
            {b.src ? (
              <img src={b.src} alt={b.name} className="badge-img" />
            ) : (
              <div className="badge-placeholder">🏅</div>
            )}
            <div className="badge-card-title">{b.name || b.id}</div>

            {/* Progress bar for logic badges */}
            {b.type === 'logic' && (
              <div>
                <div className="badge-progress" aria-hidden>
                  <div className="badge-progress-fill" style={{ width: `${Math.round((b.progress || 0) * 100)}%` }} />
                </div>
                <div className="badge-progress-text">{Math.min(totalVisits, b.required || 0)} / {b.required}</div>
              </div>
            )}

            {b.earnedAt ? <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>{new Date(b.earnedAt).toLocaleDateString()}</div> : null}
          </div>
        ))}
      </div>
      {(!Array.isArray(localBadges) || localBadges.length === 0) && (
        <div className="badges-empty">No badges yet — attend more classes to earn badges.</div>
      )}
    </div>
  );
}
